// The OAuth "Connect" flow completes inside a popup opened via `window.open`
// -- a full, separate page load of this same SPA, with its own MSW worker
// instance and its own fresh copies of every mock data module. Mutating an
// in-memory array (like organizationApplicationsList) there has no effect on
// the opener window's copy. localStorage is one of the few things actually
// shared across same-origin windows, so it's used here instead to persist
// which applications the mock OAuth callback has "connected" -- read back by
// organizationApplicationHandlers.ts when building org application responses
// in the opener window.
const STORAGE_KEY = 'mockConnectedIntegrationApplicationKeys';

const readConnectedApplicationKeys = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const isApplicationMockConnected = (
  ...applicationKeys: (string | undefined)[]
): boolean => {
  const connectedKeys = readConnectedApplicationKeys();
  return applicationKeys.some(key => key && connectedKeys.includes(key));
};

export const markApplicationMockConnected = (applicationKey: string) => {
  const connectedKeys = readConnectedApplicationKeys();
  if (!connectedKeys.includes(applicationKey)) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify([...connectedKeys, applicationKey])
      );
    } catch (error) {
      // Surfaced rather than swallowed -- otherwise the callback still
      // returns "success" while the app silently never shows as connected,
      // with nothing in the console to explain why.
      console.error(
        `Failed to persist mock connected state for "${applicationKey}":`,
        error
      );
    }
  }
};

export const markApplicationMockDisconnected = (applicationKey: string) => {
  const remainingKeys = readConnectedApplicationKeys().filter(
    key => key !== applicationKey
  );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(remainingKeys));
  } catch (error) {
    console.error(
      `Failed to persist mock disconnected state for "${applicationKey}":`,
      error
    );
  }
};
